import { FC, RefObject, useCallback, useState } from 'react';

import { push } from 'connected-react-router';
import {
    CAMPAIGN_CREATION_WIZARD_HISTORY_STATE,
    CampaignCreationWizardStep,
    getCampaignCreationWizardQuickCpcProgress,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';
import { MagritteNotificationName } from 'utils/constants/notification';

import creationWizardPlacementBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_placement_back_button_click';
import creationWizardToCampaignsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_to_campaigns_button_click';
import { quickStartForeignAreaErrorShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/common/quick_start_foreign_area_error_shown';
import { ActionBar, Button, ProgressBar, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import { startQuickCampaign } from 'components/AdQuickCreate/AdQuickCreateActionBar/startQuickCampaign';

import { selectIsVacancyForeignArea } from 'store/autoReducers';
import { WizardCpcMode } from 'store/models/campaignCreationWizard';
import {
    QuickCampaignFieldName,
    selectQuickCampaignFormDataField,
    selectQuickCampaignVacancyName,
} from 'store/models/createQuickCampaign';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignCreationWizardFlow/CampaignCreationWizardProgress.module.less';

const TrlKeys = {
    toCreateMenu: 'AdQuickCreate.toCreateMenu',
    start: 'AdQuickCreate.start',
    toAds: 'CampaignCreationWizard.actionBar.toAds',
    back: 'CampaignCreationWizard.actionBar.back',
};

export const AdQuickCreateActionBar: FC<{ actionBarRef: RefObject<HTMLDivElement> }> = ({ actionBarRef }) => {
    const isForeignArea = useSelector(selectIsVacancyForeignArea);
    const vacancyId = useSelector(selectQuickCampaignFormDataField[QuickCampaignFieldName.VacancyId]);
    const vacancyName = useSelector(selectQuickCampaignVacancyName);
    const [isSaving, setSaving] = useState(false);

    const dispatch = useDispatch();
    const { isXS, isMobile } = useBreakpoint();
    const trl = useTrls();
    const dashboardRoute = useDashboardRoute();
    const isWizardFlowEnabled = Boolean(vacancyId);
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const onQuickCampaignStart = useCallback(() => {
        if (isForeignArea) {
            quickStartForeignAreaErrorShownRaw();
            dispatch(addMagritteNotification(MagritteNotificationName.AdQuickCreateWithForeignArea));
        } else {
            void dispatch(startQuickCampaign(setSaving, dashboardRoute));
        }
    }, [dashboardRoute, dispatch, isForeignArea]);

    const handleBackClick = () => {
        creationWizardPlacementBackButtonClick({ placementType: 'CPC_QUICK' });
        openBackAlert(() =>
            goToWizardStep(CampaignCreationWizardStep.PromotionType, {
                vacancyId: Number(vacancyId) || null,
                vacancyName: vacancyName ?? undefined,
                productType: CampaignProductType.Cpc,
                cpcMode: WizardCpcMode.Quick,
            })
        );
    };

    const handleToAdsClick = () => {
        creationWizardToCampaignsButtonClick();
        openBackAlert(() => dispatch(push(dashboardRoute)));
    };

    const progressConfig = getCampaignCreationWizardQuickCpcProgress();
    const progress = (
        <ProgressBar
            progress={progressConfig.progress}
            segments={progressConfig.segments}
            data-qa="campaign-creation-wizard-progress"
        />
    );

    if (isWizardFlowEnabled) {
        return (
            <>
                <ActionBar
                    type={isXS ? 'vertical' : 'auto'}
                    isSticky
                    ref={actionBarRef}
                    showProgress={progress}
                    additionalContent={isMobile ? <div className={styles.progress}>{progress}</div> : null}
                    primaryActions={
                        <>
                            <Button
                                mode="secondary"
                                style="accent"
                                onClick={handleBackClick}
                                data-qa="ad-quick-create-wizard-back"
                            >
                                {trl(TrlKeys.back)}
                            </Button>
                            <Button
                                mode="primary"
                                style="accent"
                                onClick={onQuickCampaignStart}
                                disabled={isSaving}
                                data-qa="create"
                                loading={isSaving}
                            >
                                {trl(TrlKeys.start)}
                            </Button>
                        </>
                    }
                    secondaryActions={
                        !isMobile && (
                            <Button
                                onClick={handleToAdsClick}
                                mode="secondary"
                                style="accent"
                                data-qa="ad-quick-create-wizard-to-ads"
                            >
                                {trl(TrlKeys.toAds)}
                            </Button>
                        )
                    }
                />
                {backAlert}
            </>
        );
    }

    return (
        <ActionBar
            type={isXS ? 'vertical' : 'auto'}
            isSticky
            ref={actionBarRef}
            showDivider="always"
            primaryActions={
                <>
                    <Button
                        onClick={() => dispatch(push(CREATE_MENU_PAGE_ROUTE, CAMPAIGN_CREATION_WIZARD_HISTORY_STATE))}
                        mode="secondary"
                        style="accent"
                    >
                        {trl(TrlKeys.toCreateMenu)}
                    </Button>
                    <Button
                        mode="primary"
                        style="accent"
                        onClick={onQuickCampaignStart}
                        disabled={isSaving}
                        data-qa="create"
                    >
                        {trl(TrlKeys.start)}
                    </Button>
                </>
            }
            secondaryActions={
                <Button onClick={() => dispatch(push(dashboardRoute))} mode="secondary" style="accent">
                    {trl(TrlKeys.toAds)}
                </Button>
            }
        />
    );
};
